import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { BACKEND_URL } from "../../config";

export const inventoryApi = createApi({
  reducerPath: "inventoryApi",
  // Only the Label/SDS endpoints declare tags; every other endpoint in this
  // slice stays untagged, so adding the list is inert for them.
  tagTypes: ["LabelSds"],
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/inventory/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        // include token in req header
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
    fetchFn: async (input, init) => {
      const response = await fetch(input, init);
      if (response.status === 204) {
        return new Response(JSON.stringify([]), { status: 200 }); // Transform 204 to an empty array
      }
      return response;
    },
  }),
  endpoints: (builder) => ({
    getTotalAssignItemsInventory: builder.query({
      query: (location) =>
        location
          ? `portal/get/total-assign-items-inventory/${location}`
          : `portal/get/total-assign-items-inventory`,
      keepUnusedDataFor: Infinity,
    }),
    getLineItems: builder.query({
      query: (id) => `portal/get/line-item/${id}`,
    }),
    getAllBids: builder.query({
      query: (location) => `portal/get-items-for-bids/${location}`,
    }),
    getVendors: builder.query({
      query: (location) => `portal/get-vendors/${location}`,
    }),
    createItemBid: builder.mutation({
      query: (body) => ({
        url: "portal/create-item-bid",
        method: "POST",
        body,
      }),
    }),
    getBidsForItemLocation: builder.query({
      query: (itemLocationId) => `portal/get-bids-for-item-location/${itemLocationId}`,
    }),
    updateVendorBidsStatus: builder.mutation({
      query: (body) => ({
        url: "portal/update-vendor-bids-status",
        method: "POST",
        body,
      }),
    }),
    // Label/SDS (Levi 2026-10-27) — the native replacement for the public
    // Airtable embed the "Label/SDS" menu button used to open. The only field
    // these endpoints write is Items "Label/SDS"; the BFF re-checks the
    // "Label/SDS" TABC row on every call.
    getLabelSdsItems: builder.query({
      query: ({ search = "", category = "", limit = 25 } = {}) => ({
        url: "portal/label-sds/list",
        params: { search, category, limit },
      }),
      providesTags: ["LabelSds"],
    }),
    getLabelSdsItem: builder.query({
      query: (recid) => `portal/label-sds/get/${recid}`,
      providesTags: ["LabelSds"],
    }),
    uploadLabelSdsFile: builder.mutation({
      query: ({ recid, files }) => {
        const formData = new FormData();
        files.forEach((f) => formData.append("files", f));
        return {
          url: `portal/label-sds/upload/${recid}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["LabelSds"],
    }),
    removeLabelSdsFile: builder.mutation({
      query: ({ recid, key }) => ({
        url: `portal/label-sds/remove/${recid}`,
        method: "POST",
        body: { key },
      }),
      invalidatesTags: ["LabelSds"],
    }),
  }),
});

export const {
  useGetTotalAssignItemsInventoryQuery,
  useGetLineItemsQuery,
  useGetAllBidsQuery,
  useGetVendorsQuery,
  useCreateItemBidMutation,
  useGetBidsForItemLocationQuery,
  useUpdateVendorBidsStatusMutation,
  useGetLabelSdsItemsQuery,
  useGetLabelSdsItemQuery,
  useUploadLabelSdsFileMutation,
  useRemoveLabelSdsFileMutation,
} = inventoryApi;
